// Infra overview: how terraform modules are used across every deployed stack,
// and how current the deployed versions are against the newest one in the fleet.

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Hub as InfraIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import { useFleetModules } from '../../hooks/useFleetModules';
import { getColors } from '../../theme/tokens';
import { buildAppModuleFlows, modulePath } from '../../lib/fleetModules';
import { BarList, ChartCard, FleetLoadNote, StatTile, versionHue } from '../../components/infra/charts';
import { AppModuleFlow, CompositionGraph, LagDonut, ModuleTreemap } from '../../components/infra/diagrams';

const TOP_N = 12;

const InfraOverviewPage: React.FC = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const c = getColors(mode);
  const { fleet, progress, loading, registry } = useFleetModules();
  const { stats } = fleet;

  const flows = useMemo(() => buildAppModuleFlows(fleet, { topApps: 10, topModules: 12 }), [fleet]);
  const appCount = useMemo(() => new Set(fleet.modules.flatMap(m => m.apps)).size, [fleet.modules]);

  // Each bar is a module's calls split by version (newest strongest).
  const spread = useMemo(
    () =>
      [...fleet.modules]
        .filter(m => m.versions.length > 1)
        .sort((a, b) => b.versions.length - a.versions.length || b.usages.length - a.usages.length)
        .slice(0, TOP_N)
        .map(m => ({
          key: m.source,
          label: m.name,
          sublabel: `latest ${m.newest}`,
          value: m.versions.length,
          to: modulePath(m.source),
          segments: m.versions.map((v, i) => ({
            key: v.version,
            label: v.version,
            value: v.usages,
            color: versionHue(mode, i),
            tooltip: (
              <span>
                {m.name} <b>{v.version}</b>: {v.usages} calls in {v.jobs} jobs
              </span>
            ),
          })),
        })),
    [fleet.modules, mode]
  );

  const providers = useMemo(
    () =>
      fleet.providers.slice(0, 8).map(p => ({
        key: p.address,
        label: p.label,
        sublabel: `newest ${p.newest} · ${p.versions.length} versions`,
        value: p.jobs,
        segments: p.versions.map((v, i) => ({
          key: v.version,
          label: v.version,
          value: v.stacks,
          color: versionHue(mode, i),
          tooltip: (
            <span>
              {p.label} <b>{v.version}</b>: {v.stacks} stacks
            </span>
          ),
        })),
      })),
    [fleet.providers, mode]
  );

  const empty = !loading && stats.modules === 0;
  const twoUp = { display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2.5 } as const;

  return (
    <Box>
      <PageHeader title="Infrastructure" subtitle="Terraform modules as deployed across every stack and env/region" />
      <FleetLoadNote loaded={progress.loaded} total={progress.total} loading={loading} registry={registry} />

      {empty ? (
        <EmptyState
          icon={<InfraIcon />}
          title="No module inventory yet"
          body="Terraform jobs record their modules on each successful apply. None have reported yet."
        />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 1.5 }}>
            <StatTile label="Terraform stacks" value={stats.jobs} hint={`${stats.stackRows} env/region rows`} />
            <StatTile label="Apps" value={appCount} hint="with terraform stacks" />
            <StatTile label="Modules in use" value={stats.modules} hint={`${stats.directCalls} direct calls`} to="/infra/modules" />
            <StatTile label="On latest" value={`${Math.round(stats.currentShare * 100)}%`} hint="of direct calls" />
            <StatTile label="Version spread" value={stats.fragmented} hint="modules on 2+ versions" to="/infra/modules?sort=versions" />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(360px, 1fr) 2fr' }, gap: 2.5 }}>
            <ChartCard
              title="How current is the fleet"
              subtitle="Direct module calls in every env/region, against each module’s latest release in Spacelift"
            >
              <LagDonut counts={fleet.lagTotals} />
            </ChartCard>
            <ChartCard
              title="Module footprint"
              subtitle="Tile size is the number of jobs using the module; shade is how current its calls are. Click a tile to open it."
            >
              <ModuleTreemap modules={fleet.modules} />
            </ChartCard>
          </Box>

          <ChartCard
            title="Apps → modules"
            subtitle="Band width is the number of an app's jobs calling the module directly. Hover a node to trace it."
          >
            {flows.links.length > 0 ? (
              <AppModuleFlow flows={flows} />
            ) : (
              <Typography variant="body2" sx={{ color: c.inkMuted }}>
                No direct module calls yet.
              </Typography>
            )}
          </ChartCard>

          <ChartCard
            title="Module composition"
            subtitle="Modules whose code calls other registry modules. Line weight is how many jobs deploy that pairing."
          >
            {fleet.edges.length > 0 ? (
              <CompositionGraph edges={fleet.edges} />
            ) : (
              <Typography variant="body2" sx={{ color: c.inkMuted }}>
                No registry module calls another one.
              </Typography>
            )}
          </ChartCard>

          <Box sx={twoUp}>
            <ChartCard
              title="Widest version spread"
              subtitle="Bar length is versions deployed; segments split calls by version, newest darkest"
            >
              {spread.length > 0 ? (
                <BarList rows={spread} labelWidth={200} />
              ) : (
                <Typography variant="body2" sx={{ color: c.inkMuted }}>
                  Every module is on a single version.
                </Typography>
              )}
            </ChartCard>
            <ChartCard title="Providers" subtitle="Jobs using each provider; segments split stacks by version, newest darkest">
              <BarList rows={providers} labelWidth={200} />
            </ChartCard>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default InfraOverviewPage;
